import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicInfoLayout from '../components/public/PublicInfoLayout';

export default function AboutPage() {
  useEffect(() => {
    document.title = 'MAPP | Keller Williams Southern Africa';
  }, []);

  return (
    <PublicInfoLayout>
      <div className="space-y-8">
        {/* Hero Section */}
        <div className="rounded-2xl border border-slate-200/80 bg-white p-8 sm:p-10 shadow-sm">
          <div className="inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-red-700">
            Operations Platform
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            MAPP | Keller Williams Southern Africa
          </h1>
          <p className="mt-2 text-base sm:text-lg font-medium text-slate-600">
            Mega Agent Productivity Platform
          </p>

          <div className="mt-6 space-y-4 text-base sm:text-lg text-slate-700 leading-relaxed border-t border-slate-100 pt-6">
            <p>
              MAPP is Keller Williams Southern Africa&apos;s business operations platform for
              associates, leadership and Market Centres.
            </p>
            <p>
              It supports business workflows including:
            </p>
            <ul className="list-disc pl-6 space-y-1.5 text-base text-slate-700">
              <li><strong>listings</strong> &mdash; mandate management, listing marketing data, and multi-portal distribution</li>
              <li><strong>transactions</strong> &mdash; sales pipeline tracking, commission calculations, and Market Centre reporting</li>
              <li><strong>reporting</strong> &mdash; associate productivity metrics, capping progression, and operational dashboards</li>
              <li><strong>training</strong> &mdash; professional development materials, resource hubs, and operational guides</li>
              <li><strong>listing leads</strong> &mdash; routing and management of client inquiries from official real estate channels</li>
              <li><strong>seller and landlord communication</strong> &mdash; structured activity reports, marketing feedback, and direct email updates</li>
            </ul>
          </div>
        </div>

        {/* Google Workspace & Email Integration Section */}
        <div className="rounded-2xl border border-blue-200 bg-blue-50/70 p-8 sm:p-10 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                Google Workspace &amp; Email Integration
              </h2>
              <p className="text-sm text-slate-500">
                Client communication and reporting workflow
              </p>
            </div>
          </div>

          <div className="mt-6 space-y-4 text-base text-slate-700 leading-relaxed">
            <p>
              MAPP allows authorised Keller Williams Southern Africa associates to connect
              their approved Google Workspace account so they can send Seller and Landlord
              Update emails directly from their own mailbox.
            </p>
            <div className="rounded-xl border border-blue-200 bg-white p-4 text-sm sm:text-base text-blue-950 font-semibold">
              MAPP does not use this Gmail connection to read, search or download the
              associate&apos;s Gmail inbox or message history.
            </div>

            <div className="mt-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
                Approved Account Domains:
              </h3>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-md bg-white px-3 py-1 text-sm font-mono font-semibold text-blue-800 border border-blue-200">
                  @kwsa.co.za
                </span>
                <span className="inline-flex items-center rounded-md bg-white px-3 py-1 text-sm font-mono font-semibold text-blue-800 border border-blue-200">
                  @kwsouthafrica.co.za
                </span>
              </div>
            </div>

            <p className="text-sm text-slate-600 pt-2">
              MAPP&apos;s use and transfer of information received from Google APIs complies with the
              Google API Services User Data Policy, including the Limited Use requirements.
            </p>
          </div>
        </div>

        {/* Core Capabilities */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-700 font-semibold mb-3">
              1
            </div>
            <h3 className="font-semibold text-slate-900 text-base">Listings &amp; Marketing</h3>
            <p className="mt-2 text-sm text-slate-600 leading-normal">
              Centralised property listings, automated portal distribution, and marketing collateral generation.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-700 font-semibold mb-3">
              2
            </div>
            <h3 className="font-semibold text-slate-900 text-base">Transactions &amp; Records</h3>
            <p className="mt-2 text-sm text-slate-600 leading-normal">
              Market Centre transaction tracking, commission records, and operational auditing.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-700 font-semibold mb-3">
              3
            </div>
            <h3 className="font-semibold text-slate-900 text-base">Seller &amp; Landlord Updates</h3>
            <p className="mt-2 text-sm text-slate-600 leading-normal">
              Direct client activity reporting and performance analytics sent on behalf of authorised associates.
            </p>
          </div>
        </div>

        {/* Visible Links to Privacy Policy & Terms of Service */}
        <div className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50 p-8 shadow-sm">
          <h2 className="text-xl font-bold text-slate-900">
            Policies &amp; Legal Documentation
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Review how MAPP manages data security, user privacy, and terms of service.
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link
              to="/privacy"
              className="group flex flex-col justify-between rounded-xl border border-slate-200 bg-white p-5 shadow-xs hover:border-red-300 hover:shadow-md transition-all"
            >
              <div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-900 group-hover:text-red-700 transition-colors">
                    Privacy Policy
                  </span>
                  <span className="text-slate-400 group-hover:text-red-700 transition-colors">
                    &rarr;
                  </span>
                </div>
                <p className="mt-2 text-xs sm:text-sm text-slate-500 leading-relaxed">
                  Learn about data collection, access controls, retention, and our Google API Limited Use compliance.
                </p>
              </div>
              <span className="mt-4 text-xs font-semibold text-red-700 underline underline-offset-4">
                Read Privacy Policy
              </span>
            </Link>

            <Link
              to="/terms"
              className="group flex flex-col justify-between rounded-xl border border-slate-200 bg-white p-5 shadow-xs hover:border-red-300 hover:shadow-md transition-all"
            >
              <div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-900 group-hover:text-red-700 transition-colors">
                    Terms of Service
                  </span>
                  <span className="text-slate-400 group-hover:text-red-700 transition-colors">
                    &rarr;
                  </span>
                </div>
                <p className="mt-2 text-xs sm:text-sm text-slate-500 leading-relaxed">
                  Understand user responsibilities, acceptable use, confidentiality, and platform operational guidelines.
                </p>
              </div>
              <span className="mt-4 text-xs font-semibold text-red-700 underline underline-offset-4">
                Read Terms of Service
              </span>
            </Link>
          </div>
        </div>

        {/* Sign In Banner */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-900 px-6 py-5 text-white shadow-sm">
          <div>
            <h3 className="font-semibold text-base sm:text-lg">Are you a KWSA Associate or Staff Member?</h3>
            <p className="text-xs sm:text-sm text-slate-300 mt-0.5">
              Access is restricted to registered KWSA associates and personnel.
            </p>
          </div>
          <Link
            to="/login"
            className="inline-flex shrink-0 items-center rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-red-700 transition-colors"
          >
            Sign In to MAPP
          </Link>
        </div>
      </div>
    </PublicInfoLayout>
  );
}
